import axios from 'axios'
import { ElMessage } from 'element-plus'
import { getToken } from './token.js'

// 获取环境变量
const { VITE_API_BASE_URL, VITE_APP_ENV } = import.meta.env

// 创建 axios 实例
const service = axios.create({
  baseURL: VITE_API_BASE_URL || '/api',
  timeout: 1000000
})

// 缓存IP地址，避免重复请求
let clientIP = null

// 获取客户端IP地址
const getClientIP = async () => {
  if (clientIP) {
    return clientIP
  }
  if (window.localStorage.getItem('ip')) {
    clientIP = window.localStorage.getItem('ip')
    return clientIP
  }

  // IP获取服务列表
  const ipServices = [
    'https://ipinfo.io/json',
    'https://api64.ipify.org?format=json',
    'https://ipapi.co/json'
  ]

  // 尝试不同的IP服务
  for (const service of ipServices) {
    try {
      // 设置5秒超时
      const response = await Promise.race([
        axios.get(service),
        new Promise((_, reject) => setTimeout(() => reject(new Error('请求超时')), 5000))
      ])

      // 根据不同服务的响应格式提取IP
      let ip = ''
      if (service.includes('ipinfo.io')) {
        ip = response.data.ip
      } else if (service.includes('ipify.org')) {
        ip = response.data.ip
      } else if (service.includes('ipapi.co')) {
        ip = response.data.ip
      }

      if (ip) {
        clientIP = ip
        window.localStorage.setItem('ip', clientIP)
        return clientIP
      }
    } catch (error) {
      console.error(`获取客户端IP失败 (${service}):`, error.message || error)
      continue // 如果当前服务失败，尝试下一个服务
    }
  }

  // 所有服务都失败时返回默认值
  console.error('所有IP服务都不可用')
  return 'unknown'
}
// 开发环境打印 API 地址
if (VITE_APP_ENV === 'development') {
  console.log('🚀 当前环境:', VITE_APP_ENV)
  console.log('🌐 API 地址:', VITE_API_BASE_URL || '/api')
}

// 请求拦截器
service.interceptors.request.use(
  async (config) => {
    const token = getToken()

    // 如果是 FormData，不要设置 Content-Type，让浏览器自动设置 boundary
    if (!(config.data instanceof FormData)) {
      config.headers = {
        'Content-Type': 'application/json' //配置请求头
      }
    } else {
      // FormData 时，确保 headers 对象存在但不设置 Content-Type
      config.headers = config.headers || {}
    }
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token //配置token
    }

    // 添加客户端IP到请求头
    const ip = window.localStorage.getItem('ip') || (await getClientIP())
    config.headers['X-Client-IP'] = ip

    return config
  },
  (error) => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    const res = response
    if (res.status === 200) {
      const data = res.data
      // 统一归一化：兼容中转 ApiResponseDto（code/message/data/success camelCase）与旧格式（Success/Msg/Data PascalCase）
      if (data && typeof data === 'object') {
        return {
          ...data,
          Success: data.success !== undefined ? data.success : data.Success,
          Msg: data.message || data.msg || data.Msg || '',
          Data: data.data !== undefined ? data.data : data.Data
        }
      }
      return data
    } else {
      ElMessage.error(res.data.Msg)
      return Promise.reject(new Error(res.data.Msg))
    }
  },
  (error) => {
    if (error && error.code == 'ERR_NETWORK') {
      Notification.error('当前系统升级或服务器网络异常，请稍后重试')
      return Promise.resolve(error)
    }
    console.error('响应错误:', error)
    ElMessage.error(error.message || '网络错误')
    return Promise.reject(error)
  }
)

export default service
